import { Component } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Client, CommandStatus, FirmwareUpdateCommandDataOut, FirmwareUpdateCommandStatusDataOut, FirmwareUpdateGroupCommandsQuery, PageInfo, SortingType } from '../../../../../api/api-reference';
import { processDateRange } from '../../../../../helper/date-utils';
import { DateRangeInput } from '../../../../core/models/date-range-input';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { IsNewDirective } from '../../../../../directives/is-new.directive';
import { DatepickerComponent } from '../../../../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../../../../core/components/pagination/pagination.component';
import { SortComponent } from '../../../../core/components/sort/sort.component';
import { FirmwareUpdateGroupDetailsFilterComponent, FirmwareUpdateGroupDetailsFilterOutput } from '../../../../core/components/table/filter-panel/filters/firmware-update-group-details-filter/firmware-update-group-details-filter/firmware-update-group-details-filter.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FileService } from '../../../../../services/file.service';
import { SearchComponent } from "../../../../core/components/search/search.component";
import { ConfirmDialogComponent } from '../../../../core/components/confirm-dialog/confirm-dialog.component';
import { FirmwareUpdateComponent } from '../../../../../firmware-update/firmware-update.component';

@Component({
  selector: 'app-firmware-update-group-details',
  standalone: true,
  imports: [
    CommonModule,
    IsNewDirective,
    TranslateModule,
    AngularSvgIconModule,
    DatepickerComponent,
    FirmwareUpdateGroupDetailsFilterComponent,
    PaginationComponent,
    RouterLink,
    SortComponent,
    SearchComponent,
    ConfirmDialogComponent,
    FirmwareUpdateComponent
  ],
  templateUrl: './firmware-update-group-details.component.html',
  styleUrl: './firmware-update-group-details.component.scss'
})
export class FirmwareUpdateGroupDetailsComponent {
  public currentPage: number = 1;
  public numberOfCommands: number = 0;

  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;

  public isDatepickerOpen = false;
  public selectedDate: string | null = '';
  public data: FirmwareUpdateCommandDataOut[] = [];
  public readonly CommandStatus = CommandStatus;
  private readonly statusDisplayTranslationKeys: Record<CommandStatus, string> = {
    [CommandStatus.Pending]: 'PENDING',
    [CommandStatus.Success]: 'SUCCESS',
    [CommandStatus.Failed]: 'FAILED',
    [CommandStatus.InProgress]: 'INPROGRESS',
    [CommandStatus.Finalize]: 'FINALIZE',
    [CommandStatus.Canceled]: 'CANCELED',
    [CommandStatus.ErrorOccured]: 'FATAL_ERROR',
    [CommandStatus.Scheduled]: 'SCHEDULED',
  };

  public activeRow: number | null = null;
  public isAbortModalOpen = false;
  public selectedFirmwareStatus: FirmwareUpdateCommandStatusDataOut | null = null;
  private pendingAbortIds: string[] = [];
  private selection: Map<string, FirmwareUpdateCommandDataOut> = new Map();

  get selectedRows(): string[] {
    return Array.from(this.selection.keys());
  }

  get selectedGroupData(): FirmwareUpdateCommandDataOut[] {
    return Array.from(this.selection.values());
  }

  selectedFilters: FirmwareUpdateGroupDetailsFilterOutput = {};

  requestData = new FirmwareUpdateGroupCommandsQuery({
    firmwareUpdateGroupId: '',
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private fileService: FileService,
  ) {
    this.route.params.subscribe(params => {
      this.requestData.firmwareUpdateGroupId = params['id'];
      this.reloadTable(1);
    });
  }

  reloadTable(page?: number): void {
    this.pageNumber = page || this.currentPage;
    this.requestData.pageInfo.page = this.pageNumber;

    this.client.queryFirmwareUpdateGroupCommands(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.max(Math.ceil(this.numberOfCommands / this.pageSize), 1);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  pageChange(value: number): void {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  isAllSelected(): boolean {
    return this.data.length > 0 && this.data.every((obj) => this.isRowSelected(obj.id));
  }

  isRowSelected(id: string): boolean {
    return this.selection.has(id);
  }

  toggleAllSelected(event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      this.data.forEach((obj) => {
        if (!this.selection.has(obj.id)) {
          this.selection.set(obj.id, obj);
        }
      });
      return;
    }

    this.data.forEach((obj) => this.selection.delete(obj.id));
  }

  toggleRowSelection(row: FirmwareUpdateCommandDataOut): void {
    if (this.selection.has(row.id)) {
      this.selection.delete(row.id);
      return;
    }

    this.selection.set(row.id, row);
  }

  clearSelection(): void {
    this.selection.clear();
  }

  setActiveRow(index: number | null): void {
    this.activeRow = this.activeRow === index ? null : index;
  }

  openFirmwareUpdateDetails(row: FirmwareUpdateCommandDataOut): void {
    this.selectedFirmwareStatus = row.data ?? null;
  }

  closeFirmwareUpdateDetails(): void {
    this.selectedFirmwareStatus = null;
    this.reloadTable(this.currentPage);
  }

  retryCommand(row: FirmwareUpdateCommandDataOut): void {
    this.retryCommands([row.id]);
  }

  retrySelectedCommands(): void {
    this.retryCommands(this.selectedIdsMatching((row) => this.isRetryableCommand(row)));
  }

  private retryCommands(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update commands can be retried.');
      return;
    }

    this.client.retryFirmwareUpdateCommands(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  abortCommand(row: FirmwareUpdateCommandDataOut): void {
    this.openAbortModal([row.id]);
  }

  abortSelectedCommands(): void {
    this.openAbortModal(this.selectedIdsMatching((row) => this.isAbortableCommand(row)));
  }

  confirmAbortCommands(): void {
    const ids = [...this.pendingAbortIds];
    this.closeAbortModal();
    this.abortCommands(ids);
  }

  closeAbortModal(): void {
    this.isAbortModalOpen = false;
    this.pendingAbortIds = [];
  }

  private openAbortModal(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      return;
    }

    this.pendingAbortIds = ids;
    this.isAbortModalOpen = true;
  }

  private abortCommands(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      return;
    }

    this.client.abortFirmwareUpdateCommands(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  deleteCommand(row: FirmwareUpdateCommandDataOut): void {
    this.deleteCommands([row.id]);
  }

  deleteSelectedCommands(): void {
    this.deleteCommands(this.selectedIdsMatching((row) => this.isDeletableCommand(row)));
  }

  private deleteCommands(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update commands can be deleted.');
      return;
    }

    this.client.deleteFirmwareUpdateCommands(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));

        if (this.numberOfCommands - ids.length <= 0) {
          this.router.navigate(['/firmware-update-group']);
          return;
        }

        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  isRetryableCommand(row: FirmwareUpdateCommandDataOut): boolean {
    return row.status === CommandStatus.Failed;
  }

  isAbortableCommand(row: FirmwareUpdateCommandDataOut): boolean {
    return [
      CommandStatus.Pending,
      CommandStatus.InProgress,
      CommandStatus.Finalize,
      CommandStatus.Failed
    ].includes(row.status);
  }

  isDeletableCommand(row: FirmwareUpdateCommandDataOut): boolean {
    return row.status === CommandStatus.Scheduled;
  }

  hasSelectedRetryableCommands(): boolean {
    return this.selectedGroupData.some((row) => this.isRetryableCommand(row));
  }

  hasSelectedAbortableCommands(): boolean {
    return this.selectedGroupData.some((row) => this.isAbortableCommand(row));
  }

  hasSelectedDeletableCommands(): boolean {
    return this.selectedGroupData.some((row) => this.isDeletableCommand(row));
  }

  onSortOption(selectedSort?: SortingType): void {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  onDataReceived(event: FirmwareUpdateGroupDetailsFilterOutput): void {
    this.selectedFilters = event;
    this.requestData.assetTypeIds = event.assetTypeIds;
    this.requestData.areas = event.areas;
    this.requestData.regions = event.regions;
    this.requestData.statuses = event.statuses;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  toggleModal(): void {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.selectedDate = '';
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  progressPercent(row: FirmwareUpdateCommandDataOut): number {
    const status = row.data?.status ?? row.status;

    switch (status) {
      case CommandStatus.Pending:
        return 5;
      case CommandStatus.Failed:
      case CommandStatus.InProgress: {
        const stepsCount = row.data?.steps?.length ?? 0;
        return Math.min(85, 10 + stepsCount * 15);
      }
      case CommandStatus.Finalize:
        return 90;
      case CommandStatus.Success:
        return 100;
      default:
        return 0;
    }
  }

  statusText(status: CommandStatus | number | string): string {
    if (typeof status === 'string') return status;

    const statuses = Object.values(CommandStatus);
    return statuses[Number(status)] ?? '';
  }

  statusTranslationKey(status: CommandStatus | number | string): string {
    const statusText = this.statusText(status) as CommandStatus;
    return this.statusDisplayTranslationKeys[statusText] ?? statusText.toUpperCase();
  }

  statusClass(status: CommandStatus | number | string): string {
    return this.statusText(status).replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  downloadReport(): void {
    this.client.downloadFirmwareUpdateGroupCommands(this.requestData).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `Firmware Update Group (${this.requestData.firmwareUpdateGroupId}) Report.xlsx`,
        );
      },
      error: (err) => this.toastr.error(err),
    });
  }

  searchChange(searchTerm: string): void {
    this.requestData.search = searchTerm;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  private selectedIdsMatching(predicate: (row: FirmwareUpdateCommandDataOut) => boolean): string[] {
    return this.selectedGroupData.filter(predicate).map((row) => row.id);
  }
}
